import {IReg, REG} from "lib/commons/registry";
import {IWspUiEnv, JWspUriChangeMsg, Wsp} from "lib/wsp/wsp";
import {EWspChangesEvts, IWspXmlHouse, WspsLivePlace} from "lib/wsp/wspsLive";
import {AccelKeyMgr, ACTION, IAction} from "lib/commons/actions";
import {ESrcSt, JSrcFields, SRC, srcId, srcRef, srcUri} from "lib/wsp/src";
import {EItStatus, InfoCurrentItem, InfoHighlighItemSgn, InfoReqCurrentItem, IShortDescCtx, ITEM} from "lib/wsp/item";
import {IActionable, IContextMenuActionsPointer, IFocusActionables} from "back/commons/actionables";
import {Grid, OGridSmallInit} from "back/commons/widgets/grid-tags";
import {GridDataHolderBase, GridDataHolderJsonArray} from "back/commons/widgets/grid-libs";
import {JSX} from "lib/commons/xml/dom";
import {GridColDef, IGridDataRow, IGridLineDrawer} from "back/commons/widgets/grid-core";
import {BASIS} from "back/commons/basis";
import {ActionBtn} from "back/commons/widgets/buttons";
import {IInfo, IInfoBroker, IInfoConsumer} from "lib/commons/infos";
import {IColSearchBuilder, isColSearchBuilder, ISearchFunc} from "lib/wsp/search";
import {CellBuilderSrcIconCodeTitle, redrawSrcLine} from "back/wsp/widgets/srcGridColumns";
import {DOMSH} from "lib/commons/xml/domsh";
import {GridArrayToTree, GridDataHolderJsonTree, IGridCategorizer} from "back/commons/widgets/tree";


/**
 * wsp-src-grid : Widget des listes d'items ou tasks.
 */
export interface SrcGrid extends Grid {
	initialize(init: OSrcGridInit): this
}

export interface OSrcGridInit extends OGridSmallInit {
	reg?: IReg<IWspUiEnv>

	/** Réagira aux infos issues de ce broker (item courant, highlight d'items selon signature) */
	itemHandlingReact?: IInfoBroker

	/** Actions associées aux items sélectionnés (menu contextuel et IFocusActionables). */
	actions?: IAction<ISrcGridCtx>[]

	/** Action excutée au double-click ou sur les frappes entrée ou espace. */
	defaultAction?: IAction<ISrcGridCtx>

	/** Racourcis claviers à capter sur la sel courante. */
	accelKeyMgr?: AccelKeyMgr<ISrcGridCtx>

	/** item draggables. */
	draggable?: boolean

	/** Restreint l'affichage aux noeuds respectant ce filtre. */
	//srcFilter?: (data: JSrcFields) => boolean  NON inmplémenté!

	dataHolder?: ISrcGridDatas

	/**
	 * Si renseigné, le widget s'abonnera à cette place pour le refresh auto, sinon tentera this.reg.env.place.
	 * Si "none", le widget ne s'abonnera pas (affichages éphémères ou en masse, gestion du refresh géré par l'appelant).
	 */
	place?: WspsLivePlace | "none"

	/** Dépendance du au shortDesc ou longDesc
	 * 		Valeur par défaut : shortDesc
	 */
	onWspUriChangeScope?: 'shortDesc' | 'longDesc'

	/** Supprime le lineDrawer par défaut (avec null) ou customise le lineDrawer. */
	lineDrawer?: null | IGridLineDrawer
}

export class SrcGrid extends Grid implements ISrcGridCtx, IFocusActionables<ISrcGridCtx>, IContextMenuActionsPointer<ISrcGridCtx>, IGridLineDrawer, IColSearchBuilder, IInfoConsumer {

	reg: IReg<IWspUiEnv>;
	wsp: Wsp;

	config: OSrcGridInit;
	accelKeyMgr: AccelKeyMgr<ISrcGridCtx>;

	/** Mémoire des actions pour le menuContextuel. */
	actions?: IAction<ISrcGridCtx>[];

	/** Fournisseur des donnée courant (srcGridDatas ou celui issu du categorizer). */
		// @ts-ignore
	dataHolder: GridDataHolderBase<JSrcFields>;

	/**
	 * Datas à plat, provider pour un Categorizer ou dataHolder direct pour ce grid.
	 * A utiliser pour les mises à jour des donnnées (et pas dataHolder).
	 */
	srcGridDatas: ISrcGridDatas;

	/** API IShortDescCtx */
	shortDescs: JSrcFields[] = [];

	infoBroker: IInfoBroker;

	protected _treeView: GridArrayToTree<JSrcFields, JSrcFields>;

	/** Mémoire du listener des WspChanges pour delete. */
	protected _lstn: (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => void;

	get categorizer(): IGridCategorizer<JSrcFields, JSrcFields> {
		return this._treeView ? this._treeView.categorizer : null;
	}

	setCategorizer(categorizer: IGridCategorizer<JSrcFields, JSrcFields>, target?: GridDataHolderJsonTree<JSrcFields>) {
		if (categorizer) {
			if (!this._treeView) this._treeView = new GridArrayToTree(target).setProvider(this.srcGridDatas);
			this._treeView.setCategorizer(categorizer);
			this.dataHolder = this._treeView.target;
		} else if (this._treeView) {
			//On supprime le categorizer
			this._treeView.setProvider(null);
			this._treeView = null;
			this.dataHolder = this.srcGridDatas;
		}
	}

	selectSrcUri(srcUri: srcUri): boolean {
		return this._selectRowKey(this.srcGridDatas.findRowKeyBySrcUri(srcUri));
	}

	selectSrcId(srcId: srcId): boolean {
		return this._selectRowKey(this.srcGridDatas.findRowKeyBySrcId(srcId));
	}

	_selectRowKey(rowKey: JSrcFields): boolean {
		if (!rowKey) return false;
		if (this._treeView && this._treeView.categorizer) {
			const id = this._treeView.categorizer.getId(rowKey);
			rowKey = this._treeView.getTargetEntryById(id);
		}
		const offset = this.dataHolder.getOffset(rowKey);
		if (offset < 0) return false;
		this.setSelectedRows(offset);
		return true;
	}

	protected _initialize(init: OSrcGridInit) {
		this.config = init || {};
		this.reg = this.findReg(init);
		this.wsp = this.reg.env.wsp;
		if (!this.config.place) this.config.place = this.reg.env.place; //set default place si this.config.place != "none"
		if (!init.dataHolder) init.dataHolder = new SrcGridDatas(/*this.reg, init.srcFilter*/);
		if (!init.selType) init.selType = 'multi';
		if (!init.columnDefs) init.columnDefs = this.getDefaultColumnDefs();
		init.defaultActionCtx = this;
		this.infoBroker = init.itemHandlingReact;
		if (!('lineDrawer' in init)) init.lineDrawer = this;
		this.srcGridDatas = init.dataHolder;

		super._initialize(init);

		this.addEventListener('grid-select', function (this: SrcGrid, ev: Event) {
			this.shortDescs = this.dataHolder.getSelectedDatas();
			//console.log("spaceTree.shortDescs::::", spaceTree.shortDescs);
			if (this.matches(':focus')) this.dispatchEvent(new CustomEvent<IFocusActionables<IShortDescCtx>>('c-focus-actions', {detail: this, bubbles: true, composed: true}));
		});

		if (init.actions) this.actions = ACTION.injectSepByGroup(init.actions, this.reg.getPref("groupOrder.wsp.shortDesc", ""), this);
		if (init.accelKeyMgr) {
			this.accelKeyMgr = init.accelKeyMgr;
			this.addEventListener('keydown', (ev: KeyboardEvent) => {
				this.accelKeyMgr.handleKeyboardEvent(ev, this);
			});
		}

		if (init.itemHandlingReact) {
			const req = new InfoReqCurrentItem();
			init.itemHandlingReact.dispatchInfo(req, this);
			if (req.srcUri) this.setCurrentSrcUri(req.srcUri);
		}

		if (init.draggable) {
			this.linesNode.setAttribute("draggable", "true");
			this.linesNode.addEventListener('dragstart', function (ev: DragEvent) {
				//console.log(ev);
				ITEM.setShortDescTransferToDragSession(DOMSH.findHost<SrcGrid>(this), ev, 'build');
			});
			this.linesNode.addEventListener('dragend', ITEM.resetShortDescTransferToDragSession);
		}
	}

	getDefaultColumnDefs() {
		return [new GridColDef('srcCode')
			.setDefaultSort(1, 'ascendant')
			.setFlex('1rem', 1, 1).setMinWidth('8em').setSortable(true)
			.setCellBuilder(new CellBuilderSrcIconCodeTitle(this.reg, this.wsp.wspMetaUi, false, this.wsp.srcUriItemsSortFn))
		];
	}

	buildInitFromAtts(init?: any): any {
		init = super.buildInitFromAtts(init);
		if (this.hasAttribute("actions")) init.actions = this.reg.mergeLists(...BASIS.extractAttr(this, 'actions').split(' '));
		return init;
	}

	connectedCallback() {
		super.connectedCallback();
		if (this.infoBroker) this.infoBroker.addConsumer(this);
		if (this.config.place instanceof WspsLivePlace && this._refreshFreeze === 0) {
			this._lstn = this.onWspUriChange.bind(this);
			this.config.place.eventsMgr.on('wspUriChange', this._lstn);
		}
	}

	disconnectedCallback() {
		if (this.infoBroker) this.infoBroker.removeConsumer(this);
		if (this.config.place instanceof WspsLivePlace && this._refreshFreeze === 0) this.config.place.eventsMgr.removeListener('wspUriChange', this._lstn);
	}

	/**
	 * NOTES: A surcharger si les fields du shortDesc ne suffisent pas à rafraichir toutes les colonnes.
	 */
	onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | IWspXmlHouse | 'local' | 'server') {
		if (msg.type === EWspChangesEvts.u || msg.type === EWspChangesEvts.lcSt || msg.type === EWspChangesEvts.drfState || msg.type === EWspChangesEvts.drvState) {
			const rowKey = this.srcGridDatas.findRowKeyBySrcUri(msg.srcUri);
			if (rowKey) {
				if (this.config.onWspUriChangeScope == 'longDesc') {
					this.wsp.fetchLongDesc(SRC.srcRef(msg)).then((sd) => {
						this.srcGridDatas.refreshFields(rowKey, sd);
					})
				} else
					this.wsp.fetchShortDesc(SRC.srcRef(msg)).then((sd) => {
						this.srcGridDatas.refreshFields(rowKey, sd);
					})
			}
		} else if (msg.type === EWspChangesEvts.s) {
			const rowKey = this.srcGridDatas.findRowKeyBySrcUri(msg.srcUri);
			if (rowKey) this.srcGridDatas.refreshItSt(rowKey, msg.itSt);
		} else if (msg.type === EWspChangesEvts.r) {
			this.srcGridDatas.removeBySrcUri(msg.srcUri);
		}
	}


	protected _currentSrcUri: srcUri;
	protected _highlightSgn: RegExp;
	protected _assigned: srcRef;

	ensureRowVisibleByUri(srcUri: srcUri): boolean {
		const rowKey = this.srcGridDatas.findRowKeyBySrcUri(srcUri);
		if (rowKey) this.ensureRowVisible(this.dataHolder.getOffset(rowKey));
		return rowKey != null;
	}

	setCurrentSrcUri(srcUri: srcUri | null) {
		if (this._currentSrcUri === srcUri) return;
		this._currentSrcUri = srcUri;
		this.invalidateRows();
		//if (this._currentSrcUri) this.ensureRowVisibleByUri(this._currentSrcUri); //Ergonomiquement douteux : à rendre configuarble ?
	}

	setHighlightSgn(sgnPattern: RegExp, assigned: srcRef) {
		this._highlightSgn = sgnPattern;
		this._assigned = assigned;
		this.invalidateRows();
	}

	redrawLine(row: IGridDataRow, line: HTMLElement): void {
		redrawSrcLine(this.wsp, row.rowKey, line, this._currentSrcUri, this._highlightSgn, this._assigned);
	}

	/** boutons à afficher dans la focusToolbar. */
	protected _focusActionables: IActionable<ISrcGridCtx>[];
	get focusActionables(): IActionable<ISrcGridCtx>[] {
		if (!this._focusActionables && this.actions) this._focusActionables = ActionBtn.buildButtons<ISrcGridCtx>(this.actions, this, 'bar');
		return this._focusActionables;
	};

	get ctxMenuActions() {return {actions: this.actions, actionContext: this, rect: this.getSelRect()}}

	fillSearchColumns(map: Map<string, string | ISearchFunc>): void {
		this.wsp.getShortDescFields().forEach((f) => {map.set(f, null)});
		if (this.columns) for (const col of this.columns) {
			if (isColSearchBuilder(col.cellBuilder)) col.cellBuilder.fillSearchColumns(map);
			else if (col.cellBuilder && 'dataKey' in col.cellBuilder) map.set((col.cellBuilder as any).dataKey, null);
		}
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoCurrentItem) {
			this.setCurrentSrcUri(info.srcUri);
		} else if (info instanceof InfoHighlighItemSgn) {
			this.setHighlightSgn(info.sgnPattern, info.assigned);
		}
	}

	//*** API ISrcGridCtx / IShortDescCtx ***

	get me() {return this} // Astuce pour retrouver le SpaceTree quand l'actionContext a été surchargé.

	get emitter(): HTMLElement {return this}
}

REG.reg.registerSkin('wsp-src-grid', 1, /* language=CSS */ `
	.highlight {
		background-color: var(--row-highlight-bgcolor);
	}

	.assigned {
		background-color: var(--row-assigned-bgcolor);
	}

	.current {
		background-position: right;
		background-repeat: no-repeat;
		background-image: var(--row-current-img-end);
		background-color: var(--row-current-bgcolor);
	}
`);

customElements.define('wsp-src-grid', SrcGrid);


/**
 * Contexte pour les actions du SrcGrid.
 * Abstraction de SrcGrid pour être utilisé en contexte d'actions wrappées/surchagées.
 */
export interface ISrcGridCtx extends IShortDescCtx {
	me: SrcGrid
}

/** Api de la source de données pour un SrcGrid. */
export interface ISrcGridDatas extends GridDataHolderJsonArray<JSrcFields> {
	findRowKeyBySrcUri(srcUri: srcUri): JSrcFields;

	findRowKeyBySrcId(srcId: srcId): JSrcFields;

	removeBySrcUri(srcUri: srcUri): void;

	refreshFields(rowKey: JSrcFields, newFields: JSrcFields): void;

	refreshItSt(rowKey: JSrcFields, itSt: EItStatus): void;
}


export class SrcGridDatas extends GridDataHolderJsonArray<JSrcFields> implements ISrcGridDatas {

	useLiveSrcUri: boolean;

	constructor(/*reg: IReg<IWspUiEnv>, srcFilter?: (data: JSrcFields) => boolean*/) {
		super();
	}

	findRowKeyBySrcUri(srcUri: srcUri): JSrcFields {
		if (this.useLiveSrcUri) {
			for (const d of this._datas) if (d.srcLiveUri === srcUri || d.srcUri === srcUri) return d;
		} else {
			for (const d of this._datas) if (d.srcUri === srcUri) return d;
		}
		return null;
	}

	findRowKeyBySrcId(srcId: srcId): JSrcFields {
		for (const d of this._datas) if (d.srcId === srcId) return d;
		return null;
	}

	removeBySrcUri(srcUri: srcUri) {
		for (let i = this._datas.length - 1; i >= 0; i--) {
			if (SRC.isSubUriOrEqual(srcUri, this._datas[i].srcUri)) {
				this.updateDatas(i, 1);
			}
		}
	}

	refreshFields(rowKey: JSrcFields, newFields: JSrcFields) {
		const idx = this.getOffset(rowKey);
		if (idx >= 0) {
			if (newFields.srcSt === ESrcSt.none) {
				this.updateDatas(idx, 1);
			} else {
				this.updateDatas(idx, 1, Object.assign(Object.create(null), newFields));
			}
		}
	}

	refreshItSt(rowKey: JSrcFields, itSt: EItStatus) {
		if (this.onWillUpdate) {
			//listeners déclarés, on passe par l'api pour le dispatch et un remplacement strict de l'objet pour éviter tout effet de bord.
			const idx = this.getOffset(rowKey);
			if (idx >= 0) {
				const clone = Object.assign(Object.create(null), rowKey) as JSrcFields;
				clone.itSt = itSt;
				this.updateDatas(idx, 1, clone);
			}
		} else {
			//version optimisée XXX A mesurer...
			rowKey['itSt'] = itSt;
			this.resetRowCache(rowKey);
			const grid = this.grid;
			if (grid) grid.invalidateRows();
		}
	}
}
